import { DateTime } from 'luxon';

import CreativePrefetchSize from '@/common/models/creative/CreativePrefetchSize';
import { has, without } from '@/globals';
import MutationUtils from '@/utils/MutationUtils';

import * as types from './mutation-types';

const mutations = {
  [types.UPDATE_CREATIVE]: MutationUtils.makeUpdateListStateMutation('creatives'),

  [types.ADD_CREATIVEFILE_TO_CREATIVE](state, change) {
    const { creative, creativeFile } = change;
    creative.creativeFiles.push(creativeFile);
  },

  [types.CREATE_CREATIVE](state, creative) {
    state.newCreative = creative;
  },

  [types.DELETE_CREATIVE](state, creative) {
    state.creatives = without(state.creatives, creative);
  },

  [types.SAVE_CREATIVE](state, creative) {
    if (state.creatives.find((c) => c.id === creative.id) === undefined) {
      state.creatives = [...state.creatives, creative];
    }
  },

  [types.SAVE_CREATIVE_PREFETCH_ALL_SIZES_APPROVAL](state, { creative, approval }) {
    creative.prefetchAllSizesApproval = approval;
  },

  [types.SAVE_CREATIVE_PREFETCH_CACHE_TYPE](state, { creative, type }) {
    creative.prefetchCacheType = type;
  },

  [types.UPDATE_CREATIVE_PREFETCH_SOURCE](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.prefetchSource = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.UPDATE_CREATIVE_PREFETCH_TYPE](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.prefetchType = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.UPDATE_CREATIVE_PREFETCH_ID](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.prefetchID = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.UPDATE_CREATIVE_PREFETCH_ALL_SIZES_APPROVAL](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.prefetchAllSizesApproval = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.UPDATE_CREATIVE_PREFETCH_CACHE_TYPE](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.prefetchCacheType = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.UPDATE_CREATIVE_IS_FALLBACK](state, change) {
    const { creative } = change;
    const newValue = change.value;
    creative.isFallback = newValue;
    creative.timestamp = DateTime.local();
  },

  [types.DELETE_CREATIVEFILE](state, creativeAndCreativeFile) {
    const newCreativeFiles = creativeAndCreativeFile.creative.creativeFiles?.filter(
      (x) => x.id !== creativeAndCreativeFile.creativeFile.id,
    );
    creativeAndCreativeFile.creative.creativeFiles = newCreativeFiles;
  },

  [types.DELETE_CREATIVE_LINEITEM](state, creativeAndCreativeLineItem) {
    creativeAndCreativeLineItem.creative.lineItems = creativeAndCreativeLineItem.creative.lineItems.filter(
      (x) => x.id !== creativeAndCreativeLineItem.lineItemID,
    );
  },

  [types.SAVE_CREATIVES](state, creatives) {
    state.creatives = creatives;
  },

  [types.SAVE_EXTERNAL_CREATIVES](state, externalCreatives) {
    state.externalCreatives = externalCreatives;
  },

  [types.UPDATE_CREATIVEFILES_LIST](state, creativeFiles) {
    state.creativeFiles = creativeFiles;
  },

  [types.SAVE_CREATIVEFILE](state, creativeFile) {
    if (state.creativeFiles.find((c) => c.id === creativeFile.id) === undefined) {
      state.creativeFiles = [...state.creativeFiles, creativeFile];
    }
  },

  [types.UPDATE_CREATIVEFILE_APPROVAL_STATES](state, newStates) {
    for (const creativeFile of state.creativeFiles) {
      if (has(newStates, creativeFile.id)) {
        creativeFile.prefetchApproval = newStates[creativeFile.id];
      }
    }
  },

  [types.UPDATE_CREATIVE_PREFETCH_SIZE_APPROVAL_STATES](state, { creative, sizeApprovals }) {
    const toUpdate = new Map(sizeApprovals.map((x) => [`${x.width}x${x.height}`, x]));

    for (const prefetchSize of creative.prefetchSizes) {
      const key = `${prefetchSize.width}x${prefetchSize.height}`;

      if (toUpdate.has(key)) {
        prefetchSize.approval = toUpdate.get(key).approval;
        toUpdate.delete(key);
      }
    }

    const newEntries = [];

    for (const update of toUpdate.values()) {
      newEntries.push(new CreativePrefetchSize(update.width, update.height, update.approval));
    }

    creative.prefetchSizes = creative.prefetchSizes.concat(newEntries);
  },
};

export default mutations;
